import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Quote, Sparkles } from "lucide-react";

const LEVEL_STYLES = {
  info:     { bg: "#FAF3EA", text: "#1A1A1A", accent: "#C68E59" },
  success:  { bg: "#E9EFD7", text: "#3A4A1F", accent: "#7A8E3F" },
  warning:  { bg: "#F3DAD0", text: "#4A1F12", accent: "#B44F3F" },
  premium:  { bg: "#1A1A1A", text: "#F4E4C9", accent: "#E6C29A" },
};

/** Reads /api/dashboard-config and renders the admin-controlled widgets:
 *  - top banner (announcement-style)
 *  - quote of the day card
 * Silently absent when nothing is active. */
export default function AdminControlledWidgets() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/dashboard-config").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  if (!cfg) return null;

  const showBanner = cfg.banner?.active && cfg.banner?.message;
  const showQuote = cfg.quote_of_the_day?.active && cfg.quote_of_the_day?.text && cfg.widgets?.citation_du_jour !== false;
  const showAnnounce = cfg.announcement?.active && cfg.announcement?.message;

  if (!showBanner && !showQuote && !showAnnounce) return null;

  return (
    <div className="space-y-4 mb-8" data-testid="admin-controlled-widgets">
      {showBanner && (
        <Banner banner={cfg.banner} />
      )}
      {showAnnounce && (
        <AnnouncementStrip announcement={cfg.announcement} />
      )}
      {showQuote && (
        <QuoteCard quote={cfg.quote_of_the_day} />
      )}
    </div>
  );
}

function Banner({ banner }) {
  const styles = LEVEL_STYLES[banner.level] || LEVEL_STYLES.info;
  const Wrapper = banner.link ? Link : "div";
  const wrapperProps = banner.link ? { to: banner.link } : {};
  return (
    <Wrapper
      {...wrapperProps}
      className="block rounded-2xl p-5 sm:p-6 transition-transform hover:scale-[1.005]"
      style={{ backgroundColor: styles.bg, color: styles.text }}
      data-testid="dashboard-banner"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: `${styles.accent}22` }}>
          <Sparkles className="w-5 h-5" style={{ color: styles.accent }} />
        </div>
        <div className="flex-1 min-w-0">
          {banner.title && (
            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold" style={{ color: styles.accent }}>
              {banner.title}
            </p>
          )}
          <p className="font-serif text-lg sm:text-xl mt-1 leading-tight">{banner.message}</p>
          {banner.link && (
            <p className="text-xs mt-2 font-semibold" style={{ color: styles.accent }}>
              → Découvrir
            </p>
          )}
        </div>
      </div>
    </Wrapper>
  );
}

function AnnouncementStrip({ announcement }) {
  const Wrapper = announcement.link ? Link : "div";
  const wrapperProps = announcement.link ? { to: announcement.link } : {};
  return (
    <Wrapper
      {...wrapperProps}
      className="block rounded-2xl px-5 py-3 bg-[#FFFBF5] border border-[#EADDC9] text-sm text-[#1A1A1A] hover:border-[#C68E59] transition-colors"
      data-testid="dashboard-announcement"
    >
      <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#A26F3E] mr-2">Annonce</span>
      {announcement.message}
    </Wrapper>
  );
}

function QuoteCard({ quote }) {
  return (
    <div
      className="rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-[#FAF3EA] to-[#F3E5D0] border border-[#EADDC9]"
      data-testid="dashboard-quote"
    >
      <div className="flex items-start gap-4">
        <Quote className="w-6 h-6 text-[#C68E59] flex-shrink-0" />
        <div>
          <p className="font-serif text-base sm:text-lg text-[#1A1A1A] leading-relaxed italic">
            “{quote.text}”
          </p>
          {quote.author && (
            <p className="text-xs text-[#A26F3E] mt-2 tracking-wide">— {quote.author}</p>
          )}
        </div>
      </div>
    </div>
  );
}
